import React, { useState, useEffect } from "react";
import { motion, useScroll, useSpring, AnimatePresence } from "framer-motion";
import { Rocket } from "lucide-react";

const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const { scrollYProgress } = useScroll();

  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.pageYOffset > 300);
    };

    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  const scrollToTop = () => {
    setIsLaunching(true);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    setTimeout(() => {
      setIsLaunching(false);
    }, 1500);
  };

  return (
    <>
      <motion.div
        className="hidden lg:block fixed bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"
        style={{ scaleX }}
      />
      
      <AnimatePresence>
        {isVisible && !isLaunching && (
          <motion.button
            key="scroll-button"
            onClick={scrollToTop}
            className={`fixed bottom-10 md:bottom-5 right-5 md:right-14 p-3 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:bg-indigo-800 text-white shadow-lg transition-opacity duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500`}
            style={{
              zIndex: 9999,
              WebkitBackfaceVisibility: "hidden",
              WebkitPerspective: "1000px",
            }}
            initial={{ scale: 0, rotate: -180, y: 100 }}
            animate={{
              scale: 1,
              rotate: 0,
              y: 0,
            }}
            exit={{
              scale: 0,
              rotate: -180,
              y: 100,
              transition: { duration: 0.3 }
            }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
            }}
            whileHover={{
              scale: 1.1,
              rotate: -45,
              transition: {
                type: "spring",
                stiffness: 300,
              },
            }}
            whileTap={{
              scale: 0.9,
              rotate: -45,
              transition: {
                type: "spring",
                stiffness: 300,
              },
            }}
            aria-label="Scroll to top"
          >
            <motion.div
              className="flex items-center justify-center w-full h-full"
              animate={{
                y: [0, -5, 0],
                x: [0, 5, 0],
                rotateX: [0, 20, -20, 0],
              }}
              transition={{
                repeat: Infinity,
                duration: 1.5,
                ease: "easeInOut",
              }}
            >
              <Rocket className="h-6 w-6" strokeWidth={2} />
            </motion.div>
          </motion.button>
        )}
        {isLaunching && (
          <motion.div
            key="launching-rocket"
            className="fixed bottom-5 right-14 p-3 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white"
            style={{
              zIndex: 9999,
            }}
            initial={{ y: 0, scale: 1 }}
            animate={{
              y: -window.innerHeight, 
              scale: 1.2,
              rotate: -60, 
            }}
            transition={{
              type: "tween",
              duration: 1.5,
              ease: "easeOut",
            }}
          >
            <motion.div
              className="flex items-center justify-center"
              transition={{
                repeat: Infinity,
                duration: 0.5,
                ease: "easeInOut",
              }}
            >
              <Rocket className="h-6 w-6" strokeWidth={2} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ScrollToTopButton;
